import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Switch,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import { useErrorBoundary } from "react-error-boundary";
import { useDispatch } from "react-redux";
import PreferencesService from "../../../services/preferences.service";
import { UserPreference } from "../../../interfaces/UserPreference";
import {
  resetFilters,
  setFilters,
} from "../../../../store/filters/filters.slice";
import { Filters } from "../../../interfaces/Filters";
import { NotificationSeverities } from "../../../enums/NotificationSeverities";
import ConfirmDialog from "../../../views/confirm-dialog/ConfirmDialog";
import classes from "./ProfilePreferences.module.scss";
import useUser from "../../../hooks/useUser";
import EditPreferencesModal from "../../edit-preferences-modal/EditPreferencesModal";
import PreferenceContext from "../../../context/PreferenceContext";
import { Statuses } from "../../../enums/Statuses";
import Notification from "../../../views/notification/Notification";
import Ghost from "../../../views/ghost/Ghost";
import SubscribeButton from "../../../views/subscribe-button/SubscribeButton";
import { TipFilters } from "../../../interfaces/TipFilters";
import {
  resetAllTipFilters,
  setAllTipFilters,
} from "../../../../store/filters/tipFilters.slice";
import { ContentLengthOptions } from "../../../enums/ContentLengthOptions";

const DELETE_QUESTION = "Are you sure you want to continue?";
const SUCCESS_MESSAGE = "Successfully removed!";
const DELETE_TITLE = "Delete";

const ProfilePreferences = () => {
  const [preferences, setPreferences] = useState<UserPreference[]>([]);
  const [selectedPreferenceID, setSelectedPreferenceID] = useState<number>(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [isDeleteAllSelected, setIsDeleteAllSelected] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedEditPreference, setSelectedEditPreference] = useState<UserPreference | null>(null);
  const [status, setStatus] = useState<Statuses>(Statuses.IDLE);
  const bettingTipPreferences = useMemo(
    () => preferences.filter((p) => p.isForTipCollectionType === true),
    [preferences]
  );
  const contentPiecePreferences = useMemo(
    () => preferences.filter((p) => p.isForTipCollectionType !== true),
    [preferences]
  );
  const { preferenceID, setPreferenceID } = useContext(PreferenceContext);
  const user = useUser();
  const dispatch = useDispatch();
  const { showBoundary } = useErrorBoundary();

  const fetchPreferences = useCallback(() => {
    if (user?.email) {
      setStatus(Statuses.PENDING);
      PreferencesService.getByUserEmail(user.email)
        .then((preferences) => {
          setPreferences(preferences);
          const existingPrefernece = preferences.find((preference) => preference.id === preferenceID);

          if (existingPrefernece) {
            setSelectedPreferenceID(existingPrefernece.id);
          }

          setStatus(Statuses.SUCCESS);
        })
        .catch((error) => {
          setStatus(Statuses.ERROR);
          showBoundary(error);
        })
        .finally(() => setStatus(Statuses.IDLE));
    }
  }, [showBoundary, preferenceID, user]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences, isEditModalOpen]);

  const clearPreference = useCallback(() => {
    setSelectedPreferenceID(0);
    setPreferenceID(0);
    dispatch(resetFilters());
    dispatch(resetAllTipFilters());
  }, [dispatch, setPreferenceID]);

  const toggleHandler = useCallback((id: number, checked: boolean) => {
    if (checked) {
      setSelectedPreferenceID(id);
      const preference = preferences.find((preference) => preference.id === id);

      if (preference) {
        const tags = preference.tags.map((tag) => ({
          id: tag.id,
          attributes: { name: tag.name, type: tag.type, tagger_id: tag.tagger_id, gender: tag.gender, sport: tag.sport },
        }));

        if (preference.isForTipCollectionType === true) {
          const tipFilters: TipFilters = {
            hasWords: null,
            tags: tags,
            date: null,
            authors: [],
            contentLength: preference.contentLength ?? ContentLengthOptions.LONG,
          };
          setPreferenceID(preference.id);
          dispatch(setAllTipFilters(tipFilters));
          dispatch(resetFilters());

          return;
        }

        const filters: Filters = {
          hasWords: null,
          types: preference.types.map((type) => ({ id: type.id, name: type.name })),
          sites: preference.sites.map((site) => ({ id: site.id, name: site.name })),
          tags: tags,
          date: null,
          author: null,
          hasTips: preference.hasTips
        }

        setPreferenceID(preference.id);
        dispatch(setFilters(filters));
        dispatch(resetAllTipFilters());
      }
    } else {
      clearPreference();
    }
  }, [dispatch, preferences, clearPreference, setPreferenceID]);

  const subscribeButtonSuccessHandler = async (isSubscribed: boolean) => {
    setNotificationMessage(`Successfully ${!isSubscribed ? 'subscribed!' : 'unsubscribed!'}`);
    setIsNotificationOpen(true);
    fetchPreferences();
  };

  const deleteButtonHandler = (id: number) => {
    setSelectedPreferenceID(id);
    setIsConfirmModalOpen(true);
  };

  const onDialogConfirmHandler = useCallback(async () => {
    setStatus(Statuses.PENDING);
    if (isDeleteAllSelected) {
      if (user?.email) {
        await PreferencesService.deleteAllByUserEmail(user.email)
          .then(() => {
            setNotificationMessage(SUCCESS_MESSAGE);
            setIsNotificationOpen(true);
            clearPreference();
            setStatus(Statuses.SUCCESS);
          })
          .catch((error) => {
            setStatus(Statuses.ERROR);
            showBoundary(error);
          });
        setIsDeleteAllSelected(false);
      }
    } else {
      await PreferencesService.delete(selectedPreferenceID)
        .then(() => {
          setNotificationMessage(SUCCESS_MESSAGE);
          setIsNotificationOpen(true);
          clearPreference();
          setStatus(Statuses.SUCCESS);
        })
        .catch((error) => {
          setStatus(Statuses.ERROR);
          showBoundary(error);
        });
    }

    fetchPreferences();
    setStatus(Statuses.IDLE);
    setIsConfirmModalOpen(false);
  }, [fetchPreferences, isDeleteAllSelected, selectedPreferenceID, showBoundary, user, clearPreference]);

  const onDeleteAllButtonHandler = () => {
    setIsConfirmModalOpen(true);
    setIsDeleteAllSelected(true);
  };

  const editButtonHandler = (preference: UserPreference) => {
    setIsEditModalOpen(true);
    setSelectedEditPreference(preference);
  };

  const renderPreferencesList = (prefs: UserPreference[], title: string, showEdit: boolean = true) => {
    if (prefs.length === 0) {
      return null;
    }

    return (
      <>
        <Box className="d-flex align-items-center gap-2 mb-2">
          <Typography variant="h6" className={classes["preferences-label"]}>
            {title}
          </Typography>
          <Chip 
            label={`${prefs.length}`} 
            size="small" 
            color="success" 
            variant="outlined"
          />
        </Box>
        <List>
          {prefs.map((preference) => {
            return (
              <ListItem
                key={preference.id}
                secondaryAction={
                  <>
                    <SubscribeButton
                      preference={preference}
                      onSuccess={() =>
                        subscribeButtonSuccessHandler(
                          preference.receiveNotifications
                        )
                      }
                    />
                    <IconButton
                      edge="end"
                      onClick={() => deleteButtonHandler(preference.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </>
                }
                disablePadding
                className={preference.isForTipCollectionType ? classes["betting-tip-item"] : classes["content-piece-item"]}
              >
                <ListItemButton dense>
                  <ListItemIcon>
                    <Switch
                      classes={{
                        checked: "switch",
                        track: "switch-base",
                      }}
                      edge="start"
                      checked={selectedPreferenceID === preference.id}
                      onChange={(event, checked) =>
                        toggleHandler(preference.id, checked)
                      }
                    />
                  </ListItemIcon>
                  {showEdit && (
                    <IconButton
                      onClick={() => editButtonHandler(preference)}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  <Box className="d-flex flex-column">
                    <Typography
                      className={`fw-bold ${classes["name-text"]}`}
                    >
                      {preference.name}
                    </Typography>
                  </Box>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </>
    );
  };

  return (
    <>
      <Box className="d-flex flex-column gap-2">
        <Box className="user-card p-3 border">
          {status === Statuses.PENDING ? (
            <Box className="boxed">
              <Ghost className="profile-preferences" />
            </Box>
          ) : (
            <>
              {preferences.length === 0 ? (
                <Typography className={`h5 my-2 ${classes["preferences-label"]}`}>
                  No filters saved
                </Typography>
              ) : (
                <>
                  {renderPreferencesList(bettingTipPreferences, "Betting Tips Filters", false)}
                  
                  {bettingTipPreferences.length > 0 && contentPiecePreferences.length > 0 && (
                    <Divider className="my-3" />
                  )}
                  
                  {renderPreferencesList(contentPiecePreferences, "Content Pieces Filters", true)}
                </>
              )}
            </>
          )}
        </Box>
        <Box alignSelf="end">
          {preferences.length > 0 && (
            <Button
              className={classes["delete-button"]}
              size="large"
              variant="contained"
              disableElevation
              onClick={onDeleteAllButtonHandler}
            >
              Delete all
            </Button>
          )}
        </Box>
      </Box>
      <EditPreferencesModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        preference={selectedEditPreference}
      />
      <Notification
        open={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        message={notificationMessage}
        severity={NotificationSeverities.SUCCESS}
        position={{ vertical: "top", horizontal: "right" }}
      />
      {isConfirmModalOpen && (
        <ConfirmDialog
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={onDialogConfirmHandler}
          title={DELETE_TITLE}
          message={DELETE_QUESTION}
          status={status}
        />
      )}
    </>
  );
};

export default ProfilePreferences;
